import { Directory, Filesystem } from '@capacitor/filesystem'
import { useDatabase } from '../composables/database'
import { MessagesRows } from '../types/database'
import { Message } from '../store/useMessages'
import { useFilesystem } from '../composables/filesystem'
export interface AttachmentData {
  name: string
  id: string
}
export interface DownloadAttachmentData extends AttachmentData {
  url: string
}
type MessagesModelReturn = {
  /**Writes a message object to the db*/
  writeMessages: (message: Message[]) => Promise<void>
  /**Grabs all messages */
  getAllMessages: () => Promise<Message[]>
  /**Clears all messages from device.
   * Useful for switch to or changing reps
   */
  clearMessages: () => Promise<void>
  /**
   * Downloads attachment to attachments folder.
   * To avoid name collision, file names on system are
   * [message_id]-[file_name]
   */
  downloadAttachment: (data: DownloadAttachmentData, skip_error?: boolean) => Promise<void>
  /**Opens attachment using native viewer*/
  openAttachment: (data: AttachmentData) => Promise<void>
  /**Checks if a attachment [message_id]-[attachment_name] is on device */
  attachmentOnDevice: (data: AttachmentData) => Promise<boolean>
  /**Deletes an attachment */
  deleteAttachment: (data: AttachmentData) => Promise<void>
}
const ATTACHMENT_FOLDER = 'FHS/attachments'
const DIRECTORY = Directory.Data

/**
 * All messages are stored in a single json,
 * the only responsibility here is to read/write,
 * the entire set of messages into the db upon updates.
 */
export const MessagesModel = (): MessagesModelReturn => {
  const { query, run } = useDatabase()
  const queryMessage = async (sql: string) => {
    return await query(sql)
      .then((messages: MessagesRows | void | null) => {
        if (messages && messages[0]) {
          const data: Message[] = JSON.parse(messages[0]['messages_data'])
          const loadedMessages: Message[] = data.map((message) => {
            const { date, readDate, updatedAt, deletedAt, ...rest } = message
            return {
              ...rest,
              date: new Date(date),
              readDate: readDate ? new Date(readDate as unknown as string) : null,
              updatedAt: updatedAt ? new Date(updatedAt as unknown as string) : null,
              deletedAt: deletedAt ? new Date(deletedAt) : undefined,
            }
          })
          return loadedMessages
        } else {
          return []
        }
      })
      .catch((error) => {
        console.log(error)
        return []
      })
  }
  const writeMessages = async (messages: Message[]) => {
    const data = JSON.stringify(messages)
    const sql = `
        INSERT OR REPLACE INTO messages (id, messages_data)
        VALUES (0, ?);
    `
    await run(sql, [data]).catch((error) => {
      console.log(`Error writing messages: ${error}`)
    })
  }
  const getAllMessages = async () => {
    const sql = `
        SELECT * FROM messages;
        `
    return await queryMessage(sql)
  }
  const downloadAttachment = async (data: DownloadAttachmentData, skip_error = false) => {
    await makeAttachmentDir()
    //delete file if already there
    const fileExists = await attachmentOnDevice(data)
    if (fileExists) {
      await deleteAttachment(data)
    }
    //download file
    await useFilesystem().fetchFile(
      {
        filePath: attachmentPath(data),
        url: data.url,
      },
      skip_error
    )
  }

  const deleteAttachment = async (data: AttachmentData) => {
    await Filesystem.deleteFile({
      path: attachmentPath(data),
      directory: DIRECTORY,
    })
  }

  const openAttachment = async (data: AttachmentData) => {
    console.log('opening', attachmentPath(data))
    return await useFilesystem().openFile({
      path: attachmentPath(data),
    })
  }
  //Capacitor only
  const makeAttachmentDir = async () => {
    await Filesystem.mkdir({
      path: ATTACHMENT_FOLDER,
      directory: DIRECTORY,
      recursive: true,
    }).catch((error) => {
      if (error != 'Error: Directory exists') console.log(`Error creating directory: ${error}`)
    })
  }
  /**Check if file exists */
  const attachmentOnDevice = async (data: AttachmentData) => {
    await makeAttachmentDir()
    const fileName = attachmentPath(data).replace(ATTACHMENT_FOLDER + '/', '')
    const files = await Filesystem.readdir({
      path: ATTACHMENT_FOLDER,
      directory: DIRECTORY,
    })
    return files.files.includes(fileName)
  }
  const clearMessages = async () => {
    const sql = 'DELETE FROM messages;'
    await run(sql, []).catch((error) => console.log(`Error deleting messages: ${error}`))
  }

  //helper
  const attachmentPath = (data: AttachmentData) => {
    return `${ATTACHMENT_FOLDER}/${data.id}-${data.name}`
  }

  return {
    writeMessages,
    getAllMessages,
    clearMessages,
    downloadAttachment,
    openAttachment,
    attachmentOnDevice,
    deleteAttachment,
  }
}
